import * as React from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/use-toast"
import { formatAmount } from "@/lib/format"
import { t } from "@/lib/i18n"
import { reportWriteError } from "@/lib/writeErrors"

import { pisarCajaForecast, quitarCajaForecast } from "@/routes/finance/api"

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio",
               "agosto", "septiembre", "octubre", "noviembre", "diciembre"]

export interface CeldaDeCaja {
  pais?: string
  anio: number
  mes: number
  categoria: string
  nombre: string
  /** Lo que la proyección da hoy (o lo pisado, si ya lo está). */
  valor: number
  pisada?: { valor_usd: number; revierte_a: number | null; motivo: string | null } | null
}

/**
 * PISAR UNA CELDA FUTURA DE LA CAJA.
 *
 * Cesar, 2026-10-04: «debería de poder editar el forecast». Es el gemelo de
 * `AjustarCeldaDialog` del forecast del P&L, con dos diferencias a propósito:
 * no hay escenarios —la caja bancaria se arma sobre el moderado y punto— y lo
 * que se pisa es una CATEGORÍA de caja (cobros, planilla, impuestos…), no una
 * cuenta del plan. El motivo es obligatorio por la misma razón de siempre: una
 * cifra cambiada a mano sin por qué parece deliberada y nadie se atreve a
 * tocarla.
 */
export function PisarCeldaDialog({
  celda, open, onOpenChange,
}: { celda: CeldaDeCaja | null; open: boolean; onOpenChange: (v: boolean) => void }) {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [valor, setValor] = React.useState("")
  const [motivo, setMotivo] = React.useState("")

  React.useEffect(() => {
    if (!celda) return
    setValor(String(Math.round(celda.pisada?.valor_usd ?? celda.valor)))
    setMotivo(celda.pisada?.motivo ?? "")
  }, [celda])

  const refrescar = () => void qc.invalidateQueries({ queryKey: ["finance-caja-bancos"] })

  const guardar = useMutation({
    mutationFn: () => pisarCajaForecast({
      pais: celda!.pais, anio: celda!.anio, mes: celda!.mes, categoria: celda!.categoria,
      valor_usd: Number(valor.replace(",", ".")), motivo,
      revierte_a: celda!.pisada ? celda!.pisada.revierte_a : celda!.valor,
    }),
    onSuccess: () => {
      refrescar()
      toast({ title: t("Celda pisada"), description: t("Gana a la proyección hasta que la quites.") })
      onOpenChange(false)
    },
    onError: (e) => reportWriteError(e, { entidad: "la celda", operacion: "guardar" }),
  })
  const quitar = useMutation({
    mutationFn: () => quitarCajaForecast({ pais: celda!.pais, anio: celda!.anio, mes: celda!.mes, categoria: celda!.categoria }),
    onSuccess: () => {
      refrescar()
      toast({ title: t("Celda liberada"), description: t("Vuelve a salir de la proyección.") })
      onOpenChange(false)
    },
    onError: (e) => reportWriteError(e, { entidad: "la celda", operacion: "quitar" }),
  })

  const numero = Number(valor.replace(",", "."))
  const puedeGuardar = Number.isFinite(numero) && motivo.trim().length >= 3
  const proyectado = celda?.pisada ? celda.pisada.revierte_a : celda?.valor

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{celda ? `${t(celda.nombre)} · ${t(MESES[celda.mes - 1])} ${celda.anio}` : ""}</DialogTitle>
          <DialogDescription>
            {t("Escribe lo que sabes que va a pasar en la caja. Signo incluido: una salida va en negativo.")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md bg-surface-sunken/60 px-2.5 py-2 text-2xs">
            <span className="text-muted-foreground">{t("La proyección da")}</span>{" "}
            <span className="num-strong">{proyectado == null ? "—" : formatAmount(proyectado)}</span>
            {celda?.pisada ? (
              <>
                {" · "}
                <span className="text-muted-foreground">{t("ahora dice")}</span>{" "}
                <span className="num-strong">{formatAmount(celda.pisada.valor_usd)}</span>
              </>
            ) : null}
          </div>
          <div className="space-y-1">
            <Label htmlFor="caja-valor">{t("Cuánto, en USD")}</Label>
            <Input id="caja-valor" type="number" step="0.01" value={valor} onChange={(e) => setValor(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="caja-motivo">{t("Por qué")}</Label>
            <Textarea id="caja-motivo" rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)}
                      placeholder={t("Entra el anticipo del contrato nuevo, confirmado el 12/09")} />
            <p className="text-2xs leading-4 text-muted-foreground">{t("Queda con tu nombre y la fecha, y se ve al pie de la caja.")}</p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {celda?.pisada ? (
            <Button type="button" variant="outline" onClick={() => quitar.mutate()} disabled={quitar.isPending}>
              {t("Volver a la proyección")}
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>{t("Cancelar")}</Button>
            <Button onClick={() => guardar.mutate()} disabled={!puedeGuardar || guardar.isPending}>{t("Guardar")}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
